import { useEffect, useState } from "react";
import { IContent } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

type ContentMode = "url" | "text" | "arrayBuffer";

export interface IFileContents<T> {
  /** Presigned S3 URL for the file (available once fetched). */
  url: string | null;
  /** The fetched file body, decoded per the requested mode (`null` in "url" mode or until loaded). */
  data: T | null;
  loading: boolean;
  error: boolean;
}

/**
 * Shared data layer for the content preview viewers.
 *
 * Fetches the presigned URL for a content item from `/fileURL/{id}/{fileType}` and, unless
 * `mode` is "url", fetches the body as text or an ArrayBuffer. All viewers previously
 * duplicated this fetch + loading/error handling; centralising it here means the state-reset
 * (when `file` changes) and stale-response guards are implemented once.
 *
 * State is reset whenever `file` changes, and in-flight requests are abandoned (mounted flag +
 * AbortController) so a slow response for a previous file can't overwrite a newer one.
 */
export function useFileContents(file: IContent, mode?: "url"): IFileContents<never>;
export function useFileContents(file: IContent, mode: "text"): IFileContents<string>;
export function useFileContents(file: IContent, mode: "arrayBuffer"): IFileContents<ArrayBuffer>;
export function useFileContents(file: IContent, mode: ContentMode = "url"): IFileContents<string | ArrayBuffer | null> {
  const [url, setUrl] = useState<string | null>(null);
  const [data, setData] = useState<string | ArrayBuffer | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // Fetch the presigned URL, resetting all state on the way in so a new file never shows the
  // previous file's content, spinner, or error.
  useEffect(() => {
    let active = true;
    setUrl(null);
    setData(null);
    setError(false);
    setLoading(true);

    const fetchUrl = async (): Promise<void> => {
      try {
        const response = await AmplifyAPIWrapper.Get<{ url?: string }>(`/fileURL/${file._id}/${file.fileType}`);
        if (!active) return;
        if (response?.url) {
          setUrl(response.url);
          if (mode === "url") setLoading(false);
        } else {
          setError(true);
          setLoading(false);
        }
      } catch (err) {
        if (!active) return;
        console.error("Failed to fetch file URL:", err);
        setError(true);
        setLoading(false);
      }
    };
    void fetchUrl();

    return () => {
      active = false;
    };
  }, [file, mode]);

  // Fetch the file body (skipped in "url" mode). AbortController + mounted flag drop the result
  // if `url` changes or the component unmounts mid-flight.
  useEffect(() => {
    if (!url || mode === "url") return undefined;
    let active = true;
    const controller = new AbortController();

    const fetchContent = async (): Promise<void> => {
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const content = mode === "text" ? await response.text() : (await response.arrayBuffer()).slice(0);
        if (!active) return;
        setData(content);
        setLoading(false);
      } catch (err) {
        if (controller.signal.aborted || !active) return;
        console.error("Failed to fetch file content:", err);
        setError(true);
        setLoading(false);
      }
    };
    void fetchContent();

    return () => {
      active = false;
      controller.abort();
    };
  }, [url, mode]);

  return { url, data, loading, error };
}
